import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { BarChart3, Search, Filter, Download, ChevronLeft, ChevronRight } from 'lucide-react';

const ALL = '__all__';
const PAGE_SIZE = 20;
const COLORS = ['#DC143C', '#10B981', '#F59E0B', '#6366F1', '#06B6D4', '#EC4899'];

export default function TrainingReports() {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);

  // Inputs (lo que el usuario teclea)
  const [filterInputs, setFilterInputs] = useState({
    department_id: ALL, project_id: ALL, start_date: '', end_date: '', search: ''
  });
  // Aplicados (lo que va a la API tras clic Aplicar)
  const [appliedFilters, setAppliedFilters] = useState({
    department_id: ALL, project_id: ALL, start_date: '', end_date: '', search: ''
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadCats();
    loadStats();
    loadCharts();
    loadReports(appliedFilters);
    // eslint-disable-next-line
  }, []);

  const loadCats = async () => {
    try {
      const [d, p] = await Promise.all([api.getDepartments(), api.getProjects()]);
      setDepartments(d.data);
      setProjects(p.data);
    } catch (e) { /* */ }
  };

  const loadStats = async () => {
    try {
      const r = await api.getTrainingStats();
      setStats(r.data);
    } catch (e) { /* */ }
  };

  const loadCharts = async () => {
    try {
      const r = await api.getTrainingCharts();
      setCharts(r.data);
    } catch (e) { /* */ }
  };

  const loadReports = async (f) => {
    try {
      const params = {};
      if (f.department_id !== ALL) params.department_id = f.department_id;
      if (f.project_id !== ALL) params.project_id = f.project_id;
      if (f.start_date) params.start_date = f.start_date;
      if (f.end_date) params.end_date = f.end_date;
      if (f.search) params.search = f.search;
      const r = await api.getTrainingReports(params);
      setReports(r.data);
      setPage(1);
    } catch (e) { toast.error('Error al cargar reportes'); }
  };

  const handleApply = () => {
    if (filterInputs.start_date && filterInputs.end_date && filterInputs.start_date > filterInputs.end_date) {
      toast.error('La fecha inicio debe ser anterior a la fecha fin');
      return;
    }
    setAppliedFilters({ ...filterInputs });
    loadReports(filterInputs);
    toast.success('Filtros aplicados');
  };

  const handleClear = () => {
    const empty = { department_id: ALL, project_id: ALL, start_date: '', end_date: '', search: '' };
    setFilterInputs(empty);
    setAppliedFilters(empty);
    loadReports(empty);
  };

  const downloadPDF = async () => {
    try {
      const params = {};
      if (appliedFilters.department_id !== ALL) params.department_id = appliedFilters.department_id;
      if (appliedFilters.project_id !== ALL) params.project_id = appliedFilters.project_id;
      if (appliedFilters.start_date) params.start_date = appliedFilters.start_date;
      if (appliedFilters.end_date) params.end_date = appliedFilters.end_date;
      if (appliedFilters.search) params.search = appliedFilters.search;
      const r = await api.downloadTrainingPDF(params);
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reporte_capacitacion_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Reporte descargado');
    } catch (e) {
      toast.error('Error al descargar PDF');
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('es-MX') : '-';

  // Paginación
  const totalPages = Math.max(1, Math.ceil(reports.length / PAGE_SIZE));
  const pageReports = reports.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Charts data
  const deptChart = charts?.by_department || [];
  const projChart = charts?.by_project || [];

  return (
    <DashboardLayout>
      <div className="max-w-7xl" data-testid="training-reports-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight flex items-center gap-3">
              <BarChart3 className="h-7 w-7 text-[#DC143C]" />
              Reportes de Capacitación
            </h1>
            <p className="text-gray-600 mt-1">Progreso y resultados del personal en cursos</p>
          </div>
          <Button onClick={downloadPDF} data-testid="download-training-pdf-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2">
            <Download className="h-5 w-5" />
            Descargar PDF
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <StatCard label="Cursos" value={stats.total_courses} />
            <StatCard label="Inscritos" value={stats.total_enrollments} />
            <StatCard label="Completados" value={stats.completed} color="#10b981" />
            <StatCard label="En curso" value={stats.in_progress} color="#f59e0b" />
            <StatCard label="Promedio" value={`${stats.average_score}%`} color="#DC143C" />
          </div>
        )}

        {/* Gráficas */}
        {charts && (deptChart.length > 0 || projChart.length > 0) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card>
              <CardHeader>
                <CardTitle className="uppercase tracking-tight text-base">Progreso por Departamento</CardTitle>
                <CardDescription>Cursos completados vs. en curso</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={deptChart}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-25} textAnchor="end" height={70} interval={0} fontSize={11} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="completed" name="Completados" fill="#10B981" stackId="a" />
                    <Bar dataKey="in_progress" name="En curso" fill="#F59E0B" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="uppercase tracking-tight text-base">Progreso por Proyecto</CardTitle>
                <CardDescription>Cursos completados vs. en curso</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={projChart}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-25} textAnchor="end" height={70} interval={0} fontSize={11} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="completed" name="Completados" fill="#DC143C" stackId="a" />
                    <Bar dataKey="in_progress" name="En curso" fill="#F59E0B" stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-2 text-gray-700 text-sm font-medium uppercase tracking-wide mb-3">
            <Filter className="h-4 w-4" /> Filtros
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-3">
            <div>
              <Label className="text-xs uppercase">Departamento</Label>
              <Select value={filterInputs.department_id} onValueChange={(v) => setFilterInputs({ ...filterInputs, department_id: v })}>
                <SelectTrigger data-testid="filter-dept-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase">Proyecto</Label>
              <Select value={filterInputs.project_id} onValueChange={(v) => setFilterInputs({ ...filterInputs, project_id: v })}>
                <SelectTrigger data-testid="filter-project-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase">Desde</Label>
              <Input type="date" data-testid="training-start-date" value={filterInputs.start_date} onChange={(e) => setFilterInputs({ ...filterInputs, start_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase">Hasta</Label>
              <Input type="date" data-testid="training-end-date" value={filterInputs.end_date} onChange={(e) => setFilterInputs({ ...filterInputs, end_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input className="pl-8" placeholder="Nombre o curso..." value={filterInputs.search} onChange={(e) => setFilterInputs({ ...filterInputs, search: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleApply} data-testid="training-apply-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide flex items-center gap-2">
              <Filter className="h-4 w-4" /> Aplicar
            </Button>
            <Button onClick={handleClear} variant="outline" data-testid="training-clear-button" className="font-medium uppercase tracking-wide">Limpiar</Button>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {reports.length === 0 ? (
            <div className="p-12 text-center text-gray-500"><p>No hay registros</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Personal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Depto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Proyecto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Curso</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Puntaje</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Término</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pageReports.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{r.user_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.department_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.project_name || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{r.course_title}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full font-medium ${
                          r.status === 'completed' ? 'bg-green-50 text-green-700' :
                          r.status === 'in_progress' ? 'bg-amber-50 text-amber-700' :
                          'bg-gray-50 text-gray-700'
                        }`}>
                          {r.status === 'completed' ? 'Completado' : r.status === 'in_progress' ? 'En curso' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-bold">{r.score != null ? `${r.score}%` : '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{fmt(r.completed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginación */}
        <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-600">
          <span data-testid="training-counter">
            Mostrando {pageReports.length} de {reports.length} registros
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2" data-testid="training-pagination">
              <Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-2">Página {page} / {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

const StatCard = ({ label, value, color = '#1F2937' }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
    <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
    <p className="text-3xl font-bold mt-1" style={{ color }}>{value}</p>
  </div>
);
